import { type ImageProps as RootImageProps } from 'next/image';
import { type ComponentType } from 'react';

import { type Required } from 'utility-types';

import { type Dimensions, type SingleDimension } from '../../types/dimensions';
import { type QuantitativeSize } from '../../types/sizes';
import { quantitativeSizeToString } from '../../util/sizes';

import { getImageSize, validateAspectRatio } from './util';

export type ImageComponent = ComponentType<RootImageProps>;

export interface ImageProps extends Required<
  Omit<RootImageProps, 'height' | 'style' | 'width'>,
  'alt'
> {
  /**
   * The intrinsic aspect-ratio associated with the image that is being rendered.
   *
   * This aspect-ratio will be used to define the 'height' and 'width' attributes of the underlying
   * {@link Image} component, but not the actual height and width of the rendered image - which is
   * controlled by CSS.
   *
   * This is because height and width props for NextJS's {@link Image} component do not control the
   * rendered size of the image.  Instead, they represent the intrinsic dimensions of the image
   * (in pixels).  The 'height' and 'width' attributes are used by NextJS to make inferences about
   * the image's aspect-ratio and original size, which it uses for image rendering/loading
   * optimization.
   *
   * See:
   * 1. https://nextjs.org/docs/pages/api-reference/components/image#height
   * 2. https://nextjs.org/docs/pages/api-reference/components/image#width
   */
  readonly aspectRatio: Dimensions<number>;
  readonly component: ImageComponent;
  /**
   * The maximum height, {@link QuantitativeSize}, of the rendered image, in cases where the image
   * is being rendered in a responsive context.
   */
  readonly maxHeight?: QuantitativeSize;
  /**
   * The maximum width, {@link QuantitativeSize}, of the rendered image, in cases where the image
   * is being rendered in a responsive context.
   */
  readonly maxWidth?: QuantitativeSize;
  /**
   * The minimum height, {@link QuantitativeSize}, of the rendered image, in cases where the image
   * is being rendered in a responsive context.
   */
  readonly minHeight?: QuantitativeSize;
  /**
   * The minimum width, {@link QuantitativeSize}, of the rendered image, in cases where the image is
   * being rendered in a responsive context.
   */
  readonly minWidth?: QuantitativeSize;
  /**
   * An object that represents the size of the rendered image, {@link SingleDimension} or
   * {@link Dimensions}, expressed as an object with just a 'height' property,
   * {@link QuantitativeSize}, an object with just a 'width' property, {@link QuantitativeSize},
   * or an object with both properties, {@link Dimensions}.
   *
   * When just the 'height' is specified, the 'width' will size itself based on the image's defined
   * 'aspectRatio'.  Similarly, when just the 'width' is specified, the 'height' will size itself
   * based on the image's defined 'aspectRatio'.
   *
   * If responsive sizing is desired, this prop should be excluded.  Instead, the 'maxWidth',
   * 'minWidth', 'maxHeight' and/or 'minHeight' props should be used instead.
   */
  readonly size?: Dimensions | number | SingleDimension;
  readonly style?: Omit<
    React.CSSProperties,
    'height' | 'width' | `${'max' | 'min'}${'Height' | 'Width'}`
  >;
}

/**
 * An extension of NextJS's {@link Image} component that incorporates additional logic allowing the
 * underlying, rendered <image /> to be more easily sized based on a constant, predetermined
 * aspect-ratio.
 *
 * This component should primarily be used for immediate extension, on a per-image basis, rather
 * than direct usage throughout the application.  This allows a given image's intrinsic width,
 * height & aspect-ratio to be held constant for all cases where that image is being rendered.
 */
export const Image: React.FC<ImageProps> = ({
  aspectRatio,
  component: Component,
  maxHeight,
  maxWidth,
  minHeight,
  minWidth,
  size,
  ...props
}) => {
  validateAspectRatio(aspectRatio);
  const { height = undefined, width = undefined } =
    size === undefined ? aspectRatio : getImageSize(size);

  const getResponsiveValue = (value?: QuantitativeSize, sizedFallback?: string) =>
    value === undefined
      ? size === undefined
        ? undefined
        : sizedFallback
      : quantitativeSizeToString(value, 'px');

  return (
    <Component
      {...props}
      height={aspectRatio.height}
      style={{
        ...props.style,
        height,
        maxHeight: getResponsiveValue(maxHeight),
        maxWidth: getResponsiveValue(maxWidth, 'unset'),
        minHeight: getResponsiveValue(minHeight),
        minWidth: getResponsiveValue(minWidth),
        width,
      }}
      width={aspectRatio.width}
    />
  );
};
